/**
 * Brand/language context resolution - determines site context from URL
 * @module lib/utils/brandContext
 */

/**
 * @typedef {object} BrandContext
 * @property {string} brand - Brand identifier ('narcity' or 'mtlblog')
 * @property {string} language - Language code ('en' or 'fr')
 */

/**
 * @typedef {object} NewsletterContext
 * @property {string} brand - Brand identifier
 * @property {string} language - Language code
 * @property {string} id - Newsletter ID
 * @property {string} label - Display label
 * @property {string} shortLabel - Short display label
 * @property {string} previewSlug - Preview URL slug
 * @property {string} defaultBaseUrl - Default auth base URL for this brand
 */

/**
 * Resolve basic brand/language context from current URL
 * @returns {BrandContext}
 */
function resolveBrandContext() {
  if (typeof window === 'undefined') {
    return { brand: 'narcity', language: 'en' };
  }

  const hostname = (window.location.hostname || '').toLowerCase();
  const pathname = window.location.pathname || '';

  // Check for MTL Blog
  if (hostname.includes('mtlblog.com')) {
    return { brand: 'mtlblog', language: 'en' };
  }

  // Check for Narcity Quebec / French
  const isQuebec =
    hostname.startsWith('fr.narcity.com') ||
    pathname.startsWith('/fr/') ||
    pathname.startsWith('/qc/') ||
    pathname.startsWith('/quebec/');

  if (isQuebec) {
    return { brand: 'narcity', language: 'fr' };
  }

  // Default to Narcity Canada English
  return { brand: 'narcity', language: 'en' };
}

/**
 * Newsletter-specific context configurations by brand/language
 */
const NEWSLETTER_CONFIGS = {
  mtlblog: {
    id: 'mtlblog_trending',
    label: 'MTL Blog',
    shortLabel: 'MTL Blog',
    previewSlug: 'mtlblog',
    defaultBaseUrl: 'https://auth.mtlblog.com'
  },
  narcity_fr: {
    id: 'narcity_quebec_trending',
    label: 'Narcity Québec',
    shortLabel: 'Narcity Québec',
    previewSlug: 'narcityquebec',
    defaultBaseUrl: 'https://auth.narcity.com'
  },
  narcity_en: {
    id: 'narcity_canada_trending',
    label: 'Narcity Canada',
    shortLabel: 'Narcity Canada',
    previewSlug: 'narcitycanada',
    defaultBaseUrl: 'https://auth.narcity.com'
  }
};

/**
 * Resolve full newsletter context including brand-specific settings
 * @returns {NewsletterContext}
 */
function resolveNewsletterContext() {
  const base = resolveBrandContext();

  let configKey;
  if (base.brand === 'mtlblog') {
    configKey = 'mtlblog';
  } else if (base.language === 'fr') {
    configKey = 'narcity_fr';
  } else {
    configKey = 'narcity_en';
  }

  const config = NEWSLETTER_CONFIGS[configKey];

  return {
    brand: base.brand,
    language: base.language,
    id: config.id,
    label: config.label,
    shortLabel: config.shortLabel,
    previewSlug: config.previewSlug,
    defaultBaseUrl: config.defaultBaseUrl
  };
}

module.exports = {
  resolveBrandContext,
  resolveNewsletterContext,
  NEWSLETTER_CONFIGS
};
